/**
 * Guided spotlight tour step config.
 *
 * Steps are built dynamically from viewer context so we can:
 *  - move the deposit step early (before the challenge step) when funds are low
 *  - skip the deposit step entirely once the user has funds
 *  - skip the Plus upsell for members
 *  - add a guest-only note that signup is required for real challenges
 *  - skip any step whose target element is missing (handled by the tour UI)
 *
 * Each step targets a stable `data-tour-id` on the live screen.
 */

export const MIN_TOUR_FUNDS = 5;

const RISKY_TERMS = /\b(bet|wager|gambling|escrow|winnings|odds|sportsbook)\b/i;

export function assertGuidedTourCopy(text) {
  return !RISKY_TERMS.test(String(text || ''));
}

export function buildGuidedTourSteps({
  isGuest = false,
  hasFunds = true,
  isPlus = false,
} = {}) {
  const steps = [];

  if (isGuest) {
    steps.push({
      id: 'guest-intro',
      tourId: null,
      placement: 'center',
      title: 'Welcome to LockedIn',
      body: 'Take a quick tour. You can browse freely as a guest — sign up when you want to start real challenges.',
    });
  }

  steps.push({
    id: 'sport-pills',
    tourId: 'sport-pills',
    placement: 'bottom',
    title: 'Choose a sport',
    body: 'Filter by sport or category to find the games you care about.',
  });

  steps.push({
    id: 'game-card',
    tourId: 'game-card',
    placement: 'bottom',
    title: 'Pick a matchup',
    body: 'Browse upcoming and live games, then pick a matchup to get started.',
  });

  // Deposit step moves before the challenge step when the user has no funds.
  if (!isGuest && !hasFunds) {
    steps.push({
      id: 'wallet-deposit',
      tourId: 'wallet-deposit-button',
      placement: 'bottom',
      title: 'Add funds before joining',
      body: 'Add funds to your wallet so you can join or create real challenges.',
    });
  }

  steps.push({
    id: 'lock-it-in',
    tourId: 'lock-it-in-button',
    placement: 'top',
    title: 'Start a challenge',
    body: isGuest
      ? 'Tap “Lock It In” on any matchup. Signing up is required before you can start a real challenge.'
      : 'Tap “Lock It In” on any matchup to start a head-to-head challenge.',
  });

  steps.push({
    id: 'challenge-friend',
    tourId: 'send-ticket-button',
    placement: 'top',
    title: 'Invite someone new',
    body: 'Send a ticket to someone who is not on LockedIn yet — they open the link and join you directly. Friends in your list get an in-app challenge instead.',
  });

  // Plus upsell is hidden for members.
  if (!isPlus) {
    steps.push({
      id: 'plus-upgrade',
      tourId: 'plus-upgrade-card',
      placement: 'top',
      title: 'LockedIn Plus',
      body: 'Upgrade for lower platform fees, private leagues, and more features.',
    });
  }

  steps.push({
    id: 'my-arena',
    tourId: 'my-arena',
    placement: 'top',
    title: 'My Arena',
    body: 'Track your active, open, and completed challenges all in one place.',
  });

  steps.push({
    id: 'chat-preview',
    tourId: 'chat-preview',
    placement: 'top',
    title: 'Talk with friends',
    body: 'Chat with friends and other players, and grow your crew.',
  });

  steps.push({
    id: 'profile-menu',
    tourId: 'profile-menu',
    placement: 'bottom',
    title: 'Your account',
    body: 'Manage your wallet, withdrawals, account settings, and help from here.',
  });

  return steps;
}
